import type { Annotation, KeypointAnnotation, PhysicalMeasurements } from "@/types/annotations";
import {
  parseMeasurementEndpointName,
  type MeasurementAxisEndpointKind,
  type MeasurementDescriptor,
} from "@shared/types/measurementDescriptors";

export type AdditionalMeasurementAvgMetrics = Record<string, number | null>;
export type EntryAdditionalMeasurements = Record<string, { avg?: number; total?: number }>;

const roundToFourDecimals = (value: number): number => Math.round(value * 10000) / 10000;

export const ADDITIONAL_MEASUREMENT_LINE_COLORS = [
  "#10b981",
  "#f59e0b",
  "#a855f7",
  "#ec4899",
  "#06b6d4",
  "#14b8a6",
] as const;
export const MEASUREMENT_LABEL_LINE_HEIGHT = 1.2;
/** Estimated advance width per character, as a fraction of font size. Shared by every
 *  Konva text box we have to size ourselves, so two surfaces cannot guess differently. */
export const MEASUREMENT_LABEL_CHAR_WIDTH_RATIO = 0.6;

export const LENGTH_MEASUREMENT_LINE_COLOR = "#0000FF";
export const WIDTH_MEASUREMENT_LINE_COLOR = "#FF0000";
const MIN_MEASUREMENT_LABEL_WIDTH = 350;

/** Every color a measurement axis can be drawn in (length, width, then the cycling extras).
 *  Mask fills must avoid these so keypoints never blend into the mask. */
export const MEASUREMENT_AXIS_COLORS: readonly string[] = [
  LENGTH_MEASUREMENT_LINE_COLOR,
  WIDTH_MEASUREMENT_LINE_COLOR,
  ...ADDITIONAL_MEASUREMENT_LINE_COLORS,
];

/**
 * The color of an axis's `_end` landmark, one per axis slot.
 *
 * A `_start` dot keeps its own axis line color, so the two endpoints of an axis
 * are told apart by the accent alone — which makes this palette load-bearing for
 * the whole point-identification feature, not decoration. Every entry is
 * deliberately absent from the axis palette, `MASK_COLORS`,
 * `ANNOTATION_CLASS_COLORS`, and the editor's chrome colors, because the editor
 * paints all of those UNDERNEATH a landmark dot (see the invariant test in
 * `__tests__/annotationMaskColors.test.ts`). Two hexes can never appear here:
 * `#7C3AED` and `#4D7C0F` are class colors, which the same viewer draws as a
 * 0.4-alpha mask fill and a box outline in multi-class mode.
 *
 * Indexed by axis slot rather than by position among the endpoints, so an
 * accent cycles at exactly the period its own line color does.
 */
export const ENDPOINT_ACCENT_COLORS: readonly string[] = [
  "#C026D3", // pairs with length  #0000FF
  "#5B21B6", // pairs with width   #FF0000
  "#9F1239", // pairs with extra 0 #10b981
  "#312E81", // pairs with extra 1 #f59e0b
  "#065F46", // pairs with extra 2 #a855f7
  "#164E63", // pairs with extra 3 #ec4899
  "#7C2D12", // pairs with extra 4 #06b6d4
  "#78350F", // pairs with extra 5 #14b8a6
];

export interface MeasurementLineDrawSpec {
  key: string;
  start: KeypointAnnotation;
  end: KeypointAnnotation;
  /** The axis line color, and the `_start` dot's color. Unchanged by per-endpoint
   *  coloring — every export surface reads this and must keep rendering exactly
   *  what it always has. */
  color: string;
  /** The `_end` dot's color: this axis slot's accent. Read only through
   *  {@link describeMeasurementLineEndpoint}, so there is one way to colour a dot. */
  endColor: string;
}

/**
 * A high-contrast casing color — dark `#111827` for light inputs, `#ffffff` for dark inputs — used to
 * outline a keypoint dot or measurement line so it stays legible over any mask fill or busy imagery.
 * Decision uses WCAG relative luminance. Accepts 3- or 6-digit hex.
 */
export function getContrastingOutlineColor(hex: string): "#ffffff" | "#111827" {
  const normalized = hex.replace(/^#/, "");
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  const toLinear = (channel: number) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = toLinear(parseInt(full.slice(0, 2), 16));
  const g = toLinear(parseInt(full.slice(2, 4), 16));
  const b = toLinear(parseInt(full.slice(4, 6), 16));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? "#111827" : "#ffffff";
}

export const prettifyMeasurementKey = (key: string): string =>
  key
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

interface PartialMeasurementPair {
  start?: KeypointAnnotation;
  end?: KeypointAnnotation;
}

function measurementKeyPriority(key: string): number {
  if (key === "length") return 0;
  if (key === "width") return 1;
  return 2;
}

/** Groups named endpoints into axis keys, keeping only axes with BOTH a start and an end. */
function collectMeasurementPairs(
  keypoints: KeypointAnnotation[] | undefined
): Map<string, Required<PartialMeasurementPair>> {
  const pairs = new Map<string, PartialMeasurementPair>();

  for (const keypoint of keypoints ?? []) {
    if (!keypoint.name) continue;
    const parsed = parseMeasurementEndpointName(keypoint.name);
    if (!parsed) continue;

    const pair = pairs.get(parsed.key) ?? {};
    if (!pair[parsed.endpoint]) pair[parsed.endpoint] = keypoint;
    pairs.set(parsed.key, pair);
  }

  const complete = new Map<string, Required<PartialMeasurementPair>>();
  for (const [key, pair] of pairs) {
    if (pair.start && pair.end) complete.set(key, pair as Required<PartialMeasurementPair>);
  }
  return complete;
}

type MeasurementAxisColors = Omit<MeasurementLineDrawSpec, "start" | "end">;

/**
 * Canonical axis order (length, width, then alphabetical), each paired with the
 * colours it is drawn in. Shared by the canvas and the sidebar legend so a swatch
 * always matches the line it toggles.
 *
 * Line and dot colours are assigned together, in one pass, off a single SLOT
 * index (length 0, width 1, extras 2+). Deriving the dot colour separately — say
 * from an endpoint's position in the flattened list — would silently disagree
 * with the line whenever `length`/`width` are absent, giving a `core_length`-only
 * annotation a green line with a blue dot.
 */
function assignMeasurementAxisColors(keys: Iterable<string>): MeasurementAxisColors[] {
  const sorted = Array.from(keys).sort((left, right) => {
    const priorityDifference = measurementKeyPriority(left) - measurementKeyPriority(right);
    return priorityDifference || left.localeCompare(right);
  });

  let additionalColorIndex = 0;
  return sorted.map((key) => {
    let slot: number;
    if (key === "length") slot = 0;
    else if (key === "width") slot = 1;
    // Slot 0 and 1 are the canonical axes; the extras start after them and cycle.
    else slot = 2 + (additionalColorIndex++ % ADDITIONAL_MEASUREMENT_LINE_COLORS.length);
    return { key, color: MEASUREMENT_AXIS_COLORS[slot], endColor: ENDPOINT_ACCENT_COLORS[slot] };
  });
}

export function buildMeasurementLineDrawSpecs(keypoints: KeypointAnnotation[] | undefined): MeasurementLineDrawSpec[] {
  const pairs = collectMeasurementPairs(keypoints);
  return assignMeasurementAxisColors(pairs.keys()).map(({ key, color, endColor }) => {
    const pair = pairs.get(key);
    return { key, start: pair.start, end: pair.end, color, endColor };
  });
}

/** The display name for a measurement axis: its descriptor's label, else the prettified key. */
export function resolveMeasurementAxisLabel(axisKey: string, descriptors: MeasurementDescriptor[] = []): string {
  return descriptors.find((descriptor) => descriptor.key === axisKey)?.label ?? prettifyMeasurementKey(axisKey);
}

/** Everything the UI needs to name ONE landmark: which axis it belongs to, which
 *  end of that axis it is, and the colour of its own dot. */
export interface MeasurementEndpointIdentity {
  /** The persisted keypoint name — `core_length_start`, or a legacy `head`. */
  name: string;
  axisKey: string;
  endpoint: MeasurementAxisEndpointKind;
  /** The axis's display name, e.g. "Core Length". */
  axisLabel: string;
  /** "Start" | "End". */
  endpointLabel: string;
  /** "Core Length — Start". */
  label: string;
  color: string;
}

export interface MeasurementEndpointLegendEntry {
  key: string;
  label: string;
  /** The axis line's colour. */
  color: string;
  /** Always exactly two, start first — an axis without both is never listed. */
  endpoints: MeasurementEndpointIdentity[];
}

/**
 * Identify one end of an already-built axis spec.
 *
 * The single place the "Core Length — Start" wording is composed AND the single
 * way to obtain a landmark's dot colour, so the sidebar legend, the on-canvas
 * hover chip, and the drag HUD cannot word or colour the same landmark three
 * different ways.
 */
export function describeMeasurementLineEndpoint(
  spec: MeasurementLineDrawSpec,
  endpoint: MeasurementAxisEndpointKind,
  descriptors: MeasurementDescriptor[] = []
): MeasurementEndpointIdentity {
  const axisLabel = resolveMeasurementAxisLabel(spec.key, descriptors);
  const endpointLabel = prettifyMeasurementKey(endpoint);
  return {
    // The persisted name is authoritative: an alias-named landmark must report
    // `head`, not the `length_start` role it resolves to, or the raw-name line
    // the reviewer reads would not match what gets exported.
    name: (endpoint === "start" ? spec.start.name : spec.end.name) ?? `${spec.key}_${endpoint}`,
    axisKey: spec.key,
    endpoint,
    axisLabel,
    endpointLabel,
    label: `${axisLabel} — ${endpointLabel}`,
    color: endpoint === "start" ? spec.color : spec.endColor,
  };
}

/**
 * The measurement axes present across an annotation list, each with its two
 * endpoints, for the edit-mode sidebar legend. Colours come from the same
 * assignment the canvas uses, so a swatch matches its line and a dot swatch
 * matches its dot whenever the annotations share an axis set — which holds for
 * any single model's output.
 */
export function buildMeasurementEndpointLegend(
  annotations: Annotation[] | undefined,
  descriptors: MeasurementDescriptor[] = []
): MeasurementEndpointLegendEntry[] {
  // Union the axis keys, keeping one representative pair per axis so an
  // alias-named landmark reports the name actually persisted. Colours are left to
  // the single assignment pass below: the union may order axes differently than
  // any one annotation, so per-annotation colours would be wrong here anyway.
  const representativeByKey = new Map<string, Required<PartialMeasurementPair>>();
  for (const annotation of annotations ?? []) {
    for (const [key, pair] of collectMeasurementPairs(annotation.keypoints)) {
      if (!representativeByKey.has(key)) representativeByKey.set(key, pair);
    }
  }

  return assignMeasurementAxisColors(representativeByKey.keys()).map(({ key, color, endColor }) => {
    const pair = representativeByKey.get(key)!;
    const spec: MeasurementLineDrawSpec = { key, start: pair.start, end: pair.end, color, endColor };
    return {
      key,
      label: resolveMeasurementAxisLabel(key, descriptors),
      color,
      endpoints: [
        describeMeasurementLineEndpoint(spec, "start", descriptors),
        describeMeasurementLineEndpoint(spec, "end", descriptors),
      ],
    };
  });
}

export const formatMeasurementCm = (measurementM: number): string => (measurementM * 100).toFixed(1);

interface AnnotationMeasurementLabelOptions {
  showLength?: boolean;
  showWidth?: boolean;
  showMass?: boolean;
}

export function buildAnnotationMeasurementLabels(
  measurements: PhysicalMeasurements,
  options: AnnotationMeasurementLabelOptions = {}
): string[] {
  const { showLength = true, showWidth = true, showMass = true } = options;
  const labels: string[] = [];

  if (showLength && measurements.lengthM != null) {
    labels.push(`${formatMeasurementCm(measurements.lengthM)} cm`);
  }
  if (showWidth && measurements.widthM != null) {
    labels.push(`${formatMeasurementCm(measurements.widthM)} cm`);
  }
  if (showMass && measurements.massG != null) {
    labels.push(`${measurements.massG.toFixed(1)} g`);
  }
  for (const [key, valueMeters] of Object.entries(measurements.additionalMeasurements ?? {}).sort(([left], [right]) =>
    left.localeCompare(right)
  )) {
    labels.push(`${prettifyMeasurementKey(key)}: ${formatMeasurementCm(valueMeters)} cm`);
  }

  return labels;
}

export function annotationHasMeasurementText(measurements: PhysicalMeasurements | undefined): boolean {
  if (!measurements) return false;
  if (measurements.lengthM != null || measurements.widthM != null || measurements.massG != null) return true;
  return Object.values(measurements.additionalMeasurements ?? {}).some((value) => typeof value === "number");
}

export interface MeasurementLabelLayout {
  text: string;
  width: number;
  height: number;
}

export function buildMeasurementLabelLayout(
  labels: string[],
  fontSize: number,
  padding: number
): MeasurementLabelLayout {
  const estimatedCharacterWidth = fontSize * MEASUREMENT_LABEL_CHAR_WIDTH_RATIO;
  const longestLabelLength = Math.max(0, ...labels.map((label) => label.length));
  return {
    text: labels.join("\n"),
    width: Math.max(MIN_MEASUREMENT_LABEL_WIDTH, Math.ceil(longestLabelLength * estimatedCharacterWidth) + padding * 2),
    height: Math.ceil(labels.length * fontSize * MEASUREMENT_LABEL_LINE_HEIGHT) + padding * 2,
  };
}

export function computeVisibleAdditionalMeasurementAverages(
  annotations: Annotation[],
  descriptors: MeasurementDescriptor[]
): AdditionalMeasurementAvgMetrics {
  const visibleAnnotations = annotations.filter((annotation) => annotation.userVisibility?.isVisible !== false);
  const result: AdditionalMeasurementAvgMetrics = {};

  for (const descriptor of descriptors) {
    const values = visibleAnnotations
      .map((annotation) => annotation.measurements?.additionalMeasurements?.[descriptor.key])
      .filter((value): value is number => typeof value === "number");

    result[descriptor.key] =
      values.length > 0 ? roundToFourDecimals(values.reduce((sum, value) => sum + value, 0) / values.length) : null;
  }

  return result;
}

export function buildAdditionalMeasurementAvgFieldUpdates(
  metrics: AdditionalMeasurementAvgMetrics,
  deleteValue: unknown,
  prefix = "additionalMeasurements"
): Record<string, unknown> {
  const updates: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(metrics)) {
    updates[`${prefix}.${key}.avg`] = value === null ? deleteValue : value;
  }

  return updates;
}

export function applyAdditionalMeasurementAvgMetrics(
  existing: EntryAdditionalMeasurements | undefined,
  metrics: AdditionalMeasurementAvgMetrics
): EntryAdditionalMeasurements | undefined {
  const next: EntryAdditionalMeasurements = {};

  for (const [key, value] of Object.entries(existing ?? {})) {
    next[key] = { ...value };
  }

  for (const [key, value] of Object.entries(metrics)) {
    if (value === null) {
      if (next[key]) {
        delete next[key].avg;
        if (next[key].total === undefined) delete next[key];
      }
      continue;
    }

    next[key] = { ...(next[key] ?? {}), avg: value };
  }

  return Object.keys(next).length > 0 ? next : undefined;
}
